import { useEffect, useState } from 'react';
import { db } from '@/offline/db';
import { useProtocolStore } from '@/modules/protocol/protocol-store';
import type { EPCR, Procedure, MedicationGiven, VitalSet } from '@/types';
import { cn } from '@/lib/cn';
import {
  ClipboardCheck, BookOpen, AlertTriangle, Check,
  GitBranch, Pill, Syringe, Activity, Clock,
} from 'lucide-react';

interface Props {
  epcr: EPCR;
}

/**
 * Treatment Summary — an itemized, auditable view of all protocol-driven
 * care delivered on this ePCR. This is what feeds the narrative, the
 * printable TOC, and the RIEMSIS submission.
 */
export function TreatmentSummaryTab({ epcr }: Props) {
  const { activeProtocols, primaryProtocol, state } = useProtocolStore();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [medications, setMedications] = useState<MedicationGiven[]>([]);
  const [vitals, setVitals] = useState<VitalSet[]>([]);

  useEffect(() => {
    const load = async () => {
      const [procs, meds, vs] = await Promise.all([
        db.procedures.where('epcr_id').equals(epcr.id).sortBy('timestamp'),
        db.medications_given.where('epcr_id').equals(epcr.id).sortBy('timestamp'),
        db.vital_sets.where('epcr_id').equals(epcr.id).sortBy('timestamp'),
      ]);
      setProcedures(procs);
      setMedications(meds);
      setVitals(vs);
    };
    load();
  }, [epcr.id, state.completed_steps.length, state.decisions.length]);

  const primary = primaryProtocol();
  const allActive = activeProtocols();

  // Build a chronological event list
  type Event =
    | { kind: 'decision'; time: string; protocolName: string; stepOrder: number; question: string; choice: string }
    | { kind: 'procedure'; time: string; procedure: Procedure }
    | { kind: 'medication'; time: string; medication: MedicationGiven }
    | { kind: 'vital'; time: string; vital: VitalSet };

  const events: Event[] = [];

  for (const d of state.decisions) {
    const protocol = allActive.find((p) => p.id === d.protocol_id);
    const step = protocol?.steps.find((s) => s.order === d.step_order);
    if (protocol && step?.decision) {
      events.push({
        kind: 'decision',
        time: d.decided_at,
        protocolName: protocol.name,
        stepOrder: step.order,
        question: step.decision.question,
        choice: d.choice,
      });
    }
  }
  for (const p of procedures) events.push({ kind: 'procedure', time: p.timestamp, procedure: p });
  for (const m of medications) events.push({ kind: 'medication', time: m.timestamp, medication: m });
  for (const v of vitals) events.push({ kind: 'vital', time: v.timestamp, vital: v });

  events.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  // Deviations summary
  const deviations = state.deviations;

  // Protocol compliance stats
  const protocolCompliance = allActive.map((protocol) => {
    const total = protocol.steps.length;
    const completed = state.completed_steps.filter((c) => c.protocol_id === protocol.id).length;
    return { protocol, completed, total, pct: total > 0 ? Math.round((completed / total) * 100) : 0 };
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardCheck size={20} className="text-brand-400" />
        <h3 className="text-lg font-semibold text-slate-200">Itemized Treatment Summary</h3>
      </div>

      {/* Active Protocols */}
      <section>
        <h4 className="mb-2 text-sm font-semibold uppercase text-slate-400">Active Protocols</h4>
        {allActive.length === 0 ? (
          <div className="card text-center py-6 text-slate-500 text-sm">
            No protocols activated for this incident.
          </div>
        ) : (
          <div className="space-y-2">
            {protocolCompliance.map(({ protocol, completed, total, pct }) => (
              <div key={protocol.id} className="flex items-center gap-3 rounded-lg border border-slate-700 bg-slate-900 px-4 py-3">
                <BookOpen size={18} className={primary?.id === protocol.id ? 'text-brand-400' : 'text-slate-500'} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-200">{protocol.name}</span>
                    {primary?.id === protocol.id && (
                      <span className="rounded bg-brand-500/20 px-1.5 py-0.5 text-xs text-brand-300">PRIMARY</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {protocol.source} v{protocol.version} · {protocol.alsLevel}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-slate-200">{completed}/{total}</div>
                  <div className="text-xs text-slate-500">{pct}% complete</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Deviations */}
      {deviations.length > 0 && (
        <section>
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold uppercase text-ems-amber">
            <AlertTriangle size={14} /> Deviations from Protocol
          </h4>
          <div className="space-y-2">
            {deviations.map((d, i) => {
              const protocol = allActive.find((p) => p.id === d.protocol_id);
              return (
                <div key={i} className="rounded-lg border border-amber-900 bg-amber-950/30 p-3">
                  <div className="flex items-center justify-between text-xs text-amber-300">
                    <span>
                      {protocol?.name ?? d.protocol_id}
                      {d.step_order !== null && ` — Step ${d.step_order}`}
                    </span>
                    <span className="text-amber-500">
                      {new Date(d.documented_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-amber-200 italic">{d.reason}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Timeline */}
      <section>
        <h4 className="mb-2 text-sm font-semibold uppercase text-slate-400">Chronological Timeline</h4>
        {events.length === 0 ? (
          <div className="card text-center py-6 text-slate-500 text-sm">
            No care documented yet.
          </div>
        ) : (
          <div className="space-y-2">
            {events.map((e, idx) => (
              <TimelineRow key={idx} event={e} />
            ))}
          </div>
        )}
      </section>

      {/* Quick counts */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <CountCard label="Decisions" value={state.decisions.length} icon={<GitBranch size={18} />} />
        <CountCard label="Procedures" value={procedures.length} icon={<Syringe size={18} />} />
        <CountCard label="Medications" value={medications.length} icon={<Pill size={18} />} />
        <CountCard label="Vital Sets" value={vitals.length} icon={<Activity size={18} />} />
      </section>
    </div>
  );
}

function TimelineRow({ event }: { event: {
  kind: string; time: string;
  protocolName?: string; stepOrder?: number; question?: string; choice?: string;
  procedure?: Procedure; medication?: MedicationGiven; vital?: VitalSet;
}}) {
  const time = new Date(event.time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  if (event.kind === 'decision' && event.question && event.choice) {
    return (
      <div className="flex gap-3 rounded-lg border border-slate-700 bg-slate-900 p-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500/20 text-brand-400">
          <GitBranch size={14} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="font-mono">{time}</span>
            <span>·</span>
            <span>{event.protocolName}</span>
            <span>·</span>
            <span>Step {event.stepOrder} decision</span>
          </div>
          <div className="mt-1 text-sm text-slate-200">{event.question}</div>
          <div className="mt-1 text-sm">
            <span className="text-slate-500">Choice: </span>
            <span className={cn(
              'font-semibold',
              event.choice === 'yes' ? 'text-ems-green' : 'text-ems-red',
            )}>{event.choice.toUpperCase()}</span>
          </div>
        </div>
      </div>
    );
  }

  if (event.kind === 'procedure' && event.procedure) {
    const p = event.procedure;
    return (
      <div className="flex gap-3 rounded-lg border border-slate-700 bg-slate-900 p-3">
        <div className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          p.outcome === 'successful' ? 'bg-green-900 text-ems-green' : 'bg-slate-800 text-slate-400',
        )}>
          <Syringe size={14} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
            <span className="font-mono">{time}</span>
            {p.protocol_id && !p.is_deviation && (
              <span className="rounded bg-brand-500/20 px-1.5 py-0.5 text-brand-300">Protocol step {p.protocol_step_order}</span>
            )}
            {p.is_deviation && (
              <span className="rounded bg-amber-900 px-1.5 py-0.5 text-amber-300">DEVIATION</span>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-200">{p.procedure_type}</div>
          <div className="text-xs text-slate-500">
            {p.body_site && `at ${p.body_site} · `}
            attempt {p.attempt_number} · {p.outcome.replace('_', ' ')}
            {p.complications && ` · ${p.complications}`}
          </div>
          {p.deviation_reason && (
            <p className="mt-1 text-xs italic text-amber-400">{p.deviation_reason}</p>
          )}
        </div>
      </div>
    );
  }

  if (event.kind === 'medication' && event.medication) {
    const m = event.medication;
    return (
      <div className="flex gap-3 rounded-lg border border-slate-700 bg-slate-900 p-3">
        <div className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          m.controlled_substance ? 'bg-red-900 text-red-400' : 'bg-slate-800 text-slate-400',
        )}>
          <Pill size={14} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
            <span className="font-mono">{time}</span>
            {m.protocol_id && !m.is_deviation && (
              <span className="rounded bg-brand-500/20 px-1.5 py-0.5 text-brand-300">Protocol step {m.protocol_step_order}</span>
            )}
            {m.is_deviation && (
              <span className="rounded bg-amber-900 px-1.5 py-0.5 text-amber-300">DEVIATION</span>
            )}
            {m.controlled_substance && (
              <span className="rounded bg-red-900 px-1.5 py-0.5 text-red-300">CS</span>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-200">{m.drug_name} — {m.dose} {m.unit} {m.route}</div>
          {m.patient_response && (
            <div className="text-xs text-slate-500">Response: {m.patient_response}</div>
          )}
          {m.deviation_reason && (
            <p className="mt-1 text-xs italic text-amber-400">{m.deviation_reason}</p>
          )}
        </div>
      </div>
    );
  }

  if (event.kind === 'vital' && event.vital) {
    const v = event.vital;
    const parts: string[] = [];
    if (v.bp_systolic && v.bp_diastolic) parts.push(`BP ${v.bp_systolic}/${v.bp_diastolic}`);
    if (v.heart_rate) parts.push(`HR ${v.heart_rate}`);
    if (v.respiratory_rate) parts.push(`RR ${v.respiratory_rate}`);
    if (v.spo2) parts.push(`SpO2 ${v.spo2}%`);
    if (v.gcs_total) parts.push(`GCS ${v.gcs_total}`);
    return (
      <div className="flex gap-3 rounded-lg border border-slate-800 bg-slate-900/50 p-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-slate-400">
          <Activity size={14} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-mono">{time}</span>
            <span>·</span>
            <span>Vitals ({v.source})</span>
          </div>
          <div className="mt-0.5 text-sm text-slate-300">{parts.join(' · ')}</div>
        </div>
      </div>
    );
  }

  return null;
}

function CountCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="card flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800 text-slate-400">
        {icon}
      </div>
      <div>
        <div className="text-xl font-bold text-slate-100">{value}</div>
        <div className="text-xs text-slate-400">{label}</div>
      </div>
    </div>
  );
}
